import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Input,
  Link,
  Textarea,
} from "@amzn/awsui-components-react";
import { CopyToClipboard } from "../common/CopyToClipboard";

interface Props {
  athenaQuery: string;
  canExecute: boolean;
  createdMcmUrl: string | null;
  creatingMcm: boolean;
  disableCreateDraftMcm: boolean;
  draftMcmCreated: boolean;
  executeButtonText: string;
  loading: boolean;
  mcmError: string | null;
  mcmId: string;
  mcmWarning: string | null;
  onAthenaQueryChange: (value: string) => void;
  onCreateDraftMcm: () => void;
  onDiscard: () => void;
  onExecute: () => void;
  onSimLinkChange: (value: string) => void;
  pkList: string[];
  showDeleteSteps: boolean;
  simLink: string;
  uploadedEtag: string | null;
  uploadedS3Uri: string | null;
  usingManualPks: boolean;
}

export function DlqRedriveApprovalFlow({
  athenaQuery,
  canExecute,
  createdMcmUrl,
  creatingMcm,
  disableCreateDraftMcm,
  draftMcmCreated,
  executeButtonText,
  loading,
  mcmError,
  mcmId,
  mcmWarning,
  onAthenaQueryChange,
  onCreateDraftMcm,
  onDiscard,
  onExecute,
  onSimLinkChange,
  pkList,
  showDeleteSteps,
  simLink,
  uploadedEtag,
  uploadedS3Uri,
  usingManualPks,
}: Props) {
  const auditContextFields = (
    <SpaceBetween size="s">
      <Box variant="h4">Optional approval context</Box>
      <FormField
        label="SIM link (optional)"
        description="Included at the top of the MCM description for traceability."
      >
        <Input
          value={simLink}
          onChange={({ detail }) => onSimLinkChange(detail.value)}
          placeholder="https://issues.amazon.com/issues/TRACK-12345"
        />
      </FormField>
      <FormField
        label="Athena population query (optional)"
        description="Share the Athena query you used to identify this population. It is included in the MCM description to give the approver context."
      >
        <Textarea
          value={athenaQuery}
          onChange={({ detail }) => onAthenaQueryChange(detail.value)}
          placeholder={
            'SELECT dlq_pk\nFROM "dlq_data"."latest_dlq_event_data"\nWHERE ...'
          }
          rows={7}
        />
      </FormField>
    </SpaceBetween>
  );

  const mcmCreationControls =
    draftMcmCreated && createdMcmUrl ? (
      <SpaceBetween size="s">
        <Alert type="success" header="Draft MCM created">
          <SpaceBetween size="xs">
            <Box>
              <Link
                href={createdMcmUrl}
                external
                externalIconAriaLabel="Opens MCM in a new tab"
              >
                Open {mcmId}
              </Link>
            </Box>
            <Box>
              In MCM, send for approval and then return here to execute deletion
            </Box>
          </SpaceBetween>
        </Alert>
        {mcmWarning && (
          <Alert type="warning" header="MCM verification incomplete">
            {mcmWarning}
          </Alert>
        )}
      </SpaceBetween>
    ) : (
      <>
        <Button
          onClick={onCreateDraftMcm}
          loading={creatingMcm}
          disabled={creatingMcm || disableCreateDraftMcm}
          data-testid="create-draft-mcm"
        >
          Create Draft MCM
        </Button>
        {mcmError && (
          <Alert type="error" header="Draft MCM creation failed">
            {mcmError}
          </Alert>
        )}
      </>
    );

  if (!showDeleteSteps) {
    return (
      <Button
        onClick={onExecute}
        variant="primary"
        disabled={!canExecute || loading}
        loading={loading}
        data-testid="execute-redrive"
      >
        {executeButtonText}
      </Button>
    );
  }

  return (
    <>
      <Container
        header={<Box variant="h3">Step 1: Create and approve MCM</Box>}
      >
        <SpaceBetween size="m">
          {usingManualPks ? (
            <>
              <Box variant="p">
                Create a Draft MCM for {pkList.length} DLQ{" "}
                {pkList.length === 1 ? "pk" : "pks"}.
              </Box>
              {!draftMcmCreated && auditContextFields}
              {mcmCreationControls}
            </>
          ) : (
            <>
              {!draftMcmCreated && auditContextFields}

              {draftMcmCreated && uploadedS3Uri && (
                <ExpandableSection headerText="Uploaded file details">
                  <SpaceBetween size="s">
                    <FormField
                      label="S3 URI"
                      secondaryControl={
                        <CopyToClipboard
                          getContent={() => Promise.resolve(uploadedS3Uri)}
                          iconName="copy"
                          ariaLabel="Copy S3 URI"
                        />
                      }
                    >
                      <Input value={uploadedS3Uri} readOnly />
                    </FormField>

                    <FormField
                      label="ETag"
                      secondaryControl={
                        <CopyToClipboard
                          getContent={() => Promise.resolve(uploadedEtag ?? "")}
                          iconName="copy"
                          ariaLabel="Copy ETag"
                        />
                      }
                    >
                      <Input value={uploadedEtag ?? ""} readOnly />
                    </FormField>
                  </SpaceBetween>
                </ExpandableSection>
              )}

              {mcmCreationControls}
            </>
          )}
        </SpaceBetween>
      </Container>
      <Container
        header={<Box variant="h3">Step 2: Execute after MCM approval</Box>}
      >
        <SpaceBetween size="m">
          <Alert type="info" header="Approval happens in MCM">
            Before executing, confirm the MCM is approved and <b>In Progress</b>
            .
            {usingManualPks
              ? ""
              : " This execution reuses the prepared file above; do not upload it again."}
          </Alert>

          <SpaceBetween size="xs" direction="horizontal">
            <Button
              onClick={onExecute}
              variant="primary"
              disabled={!canExecute || loading}
              loading={loading}
              data-testid="execute-redrive"
            >
              {executeButtonText}
            </Button>
            <Button
              onClick={onDiscard}
              disabled={loading}
              data-testid="discard-redrive"
            >
              Discard
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </>
  );
}
